import React, { memo, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  useFilterState,
  useScrollPosition,
  useSortingState,
} from "../../hooks/useScrollPosition";
import { summaryColumns } from "./columnsConfig/historicalTrades";
import { smartTextFilterFn } from "./smartFilters";
import TableContainer from "./TableContainer";

const HistoricalTradesTable = ({
  data,
  title,
  headerActions = null,
  globalFilter: initialGlobalFilter = "",
  setGlobalFilter: setParentGlobalFilter,
  sorting: initialSorting = [{ id: "archive_date", desc: true }],
  setSorting: setParentSorting,
  scrollPosition: initialScrollPosition = 0,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // State Management
  const [savedGlobalFilter, setSavedGlobalFilter] = useFilterState(
    "historicalTradesTableGlobalFilter",
    initialGlobalFilter,
  );
  const [sorting, setSorting] = useSortingState(
    "historicalTradesTableSorting",
    initialSorting,
  );
  const scrollPositionHook = useScrollPosition(
    "historicalTradesTableScrollPosition",
    initialScrollPosition,
    '[class*="MuiTableContainer-root"]',
  );

  // Sync State with Parent
  useEffect(() => {
    if (setParentGlobalFilter) {
      setParentGlobalFilter(savedGlobalFilter);
    }
  }, [savedGlobalFilter, setParentGlobalFilter]);

  useEffect(() => {
    if (setParentSorting) {
      setParentSorting(sorting);
    }
  }, [sorting, setParentSorting]);

  // ENHANCED: Handle view details in drawer
  const handleViewDetails = (trade) => {
    setSelectedTrade(trade);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedTrade(null), 300);
  };

  const tableConfig = useMemo(
    () => ({
      enableColumnDragging: false,
      enableSorting: true,
      enableRowSelection: false,
      enablePagination: false,
      enableColumnFilters: true,
      // Forgiving text matching for every column without its own filter
      // (case/punctuation insensitive, see smartFilters.js).
      defaultColumn: { filterFn: smartTextFilterFn },
      enableGlobalFilter: false,
      enableDensityToggle: false,
      enableFullScreenToggle: false,
      enableHiding: false,
      muiTableBodyRowProps: ({ row }) => ({
        onClick: () => handleViewDetails(row.original),
        sx: {
          cursor: "pointer",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        },
      }),
      muiTableContainerProps: {
        sx: {
          width: "100%",
          maxHeight: {
            xs: "calc(100vh - 300px)",
            sm: "calc(100vh - 260px)",
            md: "calc(100vh - 240px)",
          },
          overflowY: "auto",
          overflowX: "auto",
        },
      },
      muiTablePaperProps: {
        elevation: 0,
        sx: {
          borderRadius: 0,
          border: "none",
        },
      },
      muiTableHeadCellProps: {
        sx: {
          fontSize: {
            xs: "0.6rem",
            sm: "0.65rem",
            md: "0.7rem",
          },
          fontWeight: 700,
          padding: {
            xs: "3px 4px",
            sm: "4px 6px",
            md: "6px 8px",
          },
          backgroundColor: theme.palette.primary.dark,
          color: theme.palette.primary.contrastText,
          borderRight: `1px solid ${theme.palette.divider}`,
          whiteSpace: "nowrap",
          "& .Mui-TableHeadCell-Content": {
            justifyContent: "space-between",
          },
        },
      },
      muiTableBodyCellProps: {
        sx: {
          fontSize: {
            xs: "0.6rem",
            sm: "0.65rem",
            md: "0.7rem",
          },
          padding: {
            xs: "2px 3px",
            sm: "3px 4px",
            md: "4px 6px",
          },
          borderRight: `1px solid ${theme.palette.divider}`,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      muiTableFooterCellProps: {
        sx: {
          fontSize: {
            xs: "0.65rem",
            sm: "0.7rem",
            md: "0.75rem",
          },
          fontWeight: 700,
          padding: {
            xs: "4px 4px",
            sm: "6px 6px",
            md: "8px 8px",
          },
          backgroundColor: theme.palette.primary.dark,
          color: theme.palette.primary.contrastText,
          borderRight: `1px solid ${theme.palette.divider}`,
          whiteSpace: "nowrap",
        },
      },
      muiTableProps: {
        sx: {
          width: "100%",
          tableLayout: "auto",
          "& .MuiTableHead-root": {
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiTableHead-root .MuiTableRow-root": {
            height: "auto",
            minHeight: {
              xs: "30px",
              sm: "35px",
            },
          },
          "& .MuiTableFooter-root": {
            borderTop: `2px solid ${theme.palette.divider}`,
            position: "sticky",
            bottom: 0,
            zIndex: 100,
            backgroundColor: theme.palette.primary.dark,
          },
          "& .MuiTableFooter-root .MuiTableRow-root": {
            height: "40px",
          },
        },
      },
      state: {
        globalFilter: savedGlobalFilter,
        sorting: sorting,
      },
      onGlobalFilterChange: setSavedGlobalFilter,
      onSortingChange: setSorting,
      initialState: {
        sorting: [{ id: "archive_date", desc: true }],
        density: "compact",
        showColumnFilters: true,
        showGlobalFilter: false,
        globalFilter: savedGlobalFilter,
        columnPinning: isDesktop
          ? {
              left: ["archive_date", "Strategy1", "Symbol"],
            }
          : {
              left: ["archive_date"],
            },
      },
    }),
    [
      theme,
      isDesktop,
      savedGlobalFilter,
      setSavedGlobalFilter,
      sorting,
      setSorting,
      scrollPositionHook.scrollPosition,
      handleViewDetails,
    ],
  );

  // Memoized columns config function (no longer needs handleViewDetails callback)
  const columnsConfigFunction = useMemo(
    () => (themeParam, globalFilterParam) =>
      summaryColumns(themeParam, globalFilterParam),
    [],
  );

  // Render
  return (
    <>
      <TableContainer
        title={title}
        customAction={headerActions}
        data={data}
        columnsConfig={columnsConfigFunction}
        globalFilter={savedGlobalFilter}
        setGlobalFilter={setSavedGlobalFilter}
        sorting={sorting}
        setSorting={setSorting}
        scrollPosition={scrollPositionHook.scrollPosition}
        tableConfig={tableConfig}
      />

      {/* ENHANCED: Side Drawer for Details */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 600 },
            p: 0,
          },
        }}
      >
        {selectedTrade && (
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                backgroundColor: theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6">Trade Details</Typography>
                <Typography variant="caption">
                  {selectedTrade.CustomTradeID ||
                    selectedTrade.original_trade_id}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseDrawer} sx={{ color: "inherit" }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
              <Grid container spacing={3}>
                {/* Summary */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="h5" gutterBottom>
                      {selectedTrade.Symbol}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      <Chip
                        label={selectedTrade.PosAction}
                        size="small"
                        color={
                          selectedTrade.PosAction === "Buy"
                            ? "success"
                            : "error"
                        }
                      />
                      <Chip
                        label={selectedTrade.StrategyType || "N/A"}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="h6" color="primary">
                      {selectedTrade.FilledAmt} @ $
                      {selectedTrade.TradeAvgPrice?.toFixed(4)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Identifiers */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Identifiers
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Trade ID:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.CustomTradeID || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Original ID:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.original_trade_id || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        ISIN:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.Isin || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Bloomberg Ticker:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.BB_Ticker_Code || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Strategy */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Strategy
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Type:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.StrategyType || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Name:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.Strategy1 || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Broker:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.BrokerCode || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Security Details */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Security Details
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Type:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.SecType || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Type 2:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.sec_type2 || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Market Value:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        ${selectedTrade.MV?.toFixed(2) || "0.00"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Net Money:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        ${selectedTrade.net_money?.toFixed(2) || "0.00"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Multipliers */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Multipliers
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Price:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.Price_Multiplier || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Position:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.Position_Multiplier || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Metadata */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Timestamps
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Last Update:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.last_update || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Created:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.created_at
                          ? new Date(selectedTrade.created_at).toLocaleString()
                          : "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Updated:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTrade.updated_at
                          ? new Date(selectedTrade.updated_at).toLocaleString()
                          : "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Additional IDs */}
                {((selectedTrade.CustomId1 &&
                  selectedTrade.CustomId1 !== "nan") ||
                  (selectedTrade.CustomId2 &&
                    selectedTrade.CustomId2 !== "nan") ||
                  (selectedTrade.CustomId3 &&
                    selectedTrade.CustomId3 !== "nan") ||
                  (selectedTrade.Alias2 && selectedTrade.Alias2 !== "")) && (
                  <>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Additional IDs
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {selectedTrade.CustomId1 &&
                          selectedTrade.CustomId1 !== "nan" && (
                            <Chip
                              label={`ID1: ${selectedTrade.CustomId1}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        {selectedTrade.CustomId2 &&
                          selectedTrade.CustomId2 !== "nan" && (
                            <Chip
                              label={`ID2: ${selectedTrade.CustomId2}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        {selectedTrade.CustomId3 &&
                          selectedTrade.CustomId3 !== "nan" && (
                            <Chip
                              label={`ID3: ${selectedTrade.CustomId3}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        {selectedTrade.Alias2 &&
                          selectedTrade.Alias2 !== "" && (
                            <Chip
                              label={`Alias: ${selectedTrade.Alias2}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Box>
        )}
      </Drawer>
    </>
  );
};

export default memo(HistoricalTradesTable);
